import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import useApiHelper from "../../../hooks/useApiHelper";
import LoadingWheel from "../../shared/LoadingWheel";

/**
 * The CSV column names the API's import/export maps use. These strings must stay in sync with
 * CustomerGenericPriceSettingImportMap / CustomerGenericPriceSettingExportMap - the user's own
 * column names are mapped onto these before the file is sent, which is what lets them lay their
 * spreadsheet out however they like.
 */
type FieldType = "string" | "number" | "boolean" | "bodyCondition";

interface CsvField {
  /** Header name the API expects. */
  name: string;
  label: string;
  type: FieldType;
  hint?: string;
  /** Extra header spellings we'll auto-detect from the user's file. */
  aliases?: string[];
}

interface FieldGroup {
  title: string;
  className: string;
  fields: CsvField[];
}

const FIELD_GROUPS: FieldGroup[] = [
  {
    title: "Identity",
    className: "bg-gray-100",
    fields: [
      {
        name: "RuleId",
        label: "ID",
        type: "string",
        hint: "Leave a row's ID blank to add a new rule. Fill it in to update the existing rule.",
        aliases: ["id", "rule", "ruleref", "reference"],
      },
    ],
  },
  {
    title: "Rule Only Applies When ALL These Conditions Match",
    className: "bg-cyan-50",
    fields: [
      { name: "FromYear", label: "From Year", type: "number", aliases: ["yearfrom", "minyear"] },
      { name: "ToYear", label: "To Year", type: "number", aliases: ["yearto", "maxyear"] },
      { name: "Make", label: "Make", type: "string", aliases: ["manufacturer"] },
      { name: "Model", label: "Model", type: "string" },
      { name: "FuelType", label: "Fuel Type", type: "string", aliases: ["fuel"] },
      { name: "TransmissionType", label: "Transmission", type: "string", aliases: ["transmission", "gearbox"] },
      { name: "EngineCode", label: "Engine Code", type: "string", aliases: ["engine"] },
      {
        name: "EngineCodeMatchType",
        label: "Engine Code Match",
        type: "string",
        hint: "Contains, StartsWith or Exact. Blank leaves it unchanged (new rules use Contains).",
        aliases: ["enginematch", "matchtype", "enginecodematch"],
      },
      { name: "FromEngineCc", label: "From CC", type: "number", aliases: ["ccfrom", "mincc", "enginecefrom"] },
      { name: "ToEngineCc", label: "To CC", type: "number", aliases: ["ccto", "maxcc"] },
      {
        name: "MinBodyCondition",
        label: "Min Body",
        type: "bodyCondition",
        hint: "Good, Average, or Any.",
        aliases: ["minbody", "bodycondition", "condition"],
      },
    ],
  },
  {
    title: "Fixed Price (Overrides Top Up)",
    className: "bg-yellow-50",
    fields: [
      { name: "GoodEngineFixedPrice", label: "Good Engine", type: "number", aliases: ["fixedgoodengine"] },
      { name: "MinorFaultFixedPrice", label: "Minor Fault", type: "number", aliases: ["fixedminorfault"] },
      { name: "MajorFaultFixedPrice", label: "Major Fault", type: "number", aliases: ["fixedmajorfault"] },
      { name: "FixedPrice", label: "Fixed Price (all)", type: "number", aliases: ["fixed"] },
    ],
  },
  {
    title: "Top Up / Deduction (+/-)",
    className: "bg-blue-50",
    fields: [
      { name: "MaximumPrice", label: "Max Price", type: "number", aliases: ["maxprice", "cap"] },
      { name: "GoodEnginePriceSetting.Value", label: "Good Engine", type: "number", aliases: ["goodengine", "goodenginetopup"] },
      {
        name: "GoodEnginePriceSetting.IsFixed",
        label: "Good Engine is £ (not %)",
        type: "boolean",
        hint: "TRUE for a £ amount, FALSE for a percentage.",
        aliases: ["goodengineisfixed", "goodenginepound"],
      },
      { name: "MinorFaultPriceSetting.Value", label: "Minor Fault", type: "number", aliases: ["minorfault", "minorfaulttopup"] },
      {
        name: "MinorFaultPriceSetting.IsFixed",
        label: "Minor Fault is £ (not %)",
        type: "boolean",
        aliases: ["minorfaultisfixed", "minorfaultpound"],
      },
      { name: "MajorFaultPriceSetting.Value", label: "Major Fault", type: "number", aliases: ["majorfault", "majorfaulttopup"] },
      {
        name: "MajorFaultPriceSetting.IsFixed",
        label: "Major Fault is £ (not %)",
        type: "boolean",
        aliases: ["majorfaultisfixed", "majorfaultpound"],
      },
      { name: "PriceSetting.Value", label: "Base Top Up", type: "number", aliases: ["basevalue", "pricesettingvalue"] },
      { name: "PriceSetting.IsFixed", label: "Base Top Up is £ (not %)", type: "boolean", aliases: ["baseisfixed"] },
    ],
  },
  {
    title: "Other",
    className: "bg-gray-100",
    fields: [
      { name: "PartsLookup", label: "Part Pricing", type: "boolean", aliases: ["parts", "partpricing"] },
      { name: "IsActive", label: "Is Active", type: "boolean", aliases: ["active", "enabled"] },
      {
        name: "IsExclusive",
        label: "Exclusive",
        type: "boolean",
        hint: "TRUE to add only this rule's top up when it matches.",
        aliases: ["onlythis"],
      },
      { name: "AppliesToScrap", label: "Applies To Scrap", type: "boolean", aliases: ["scrap"] },
      { name: "AppliesToSalvage", label: "Applies To Salvage", type: "boolean", aliases: ["salvage"] },
      { name: "AppliesToSmart", label: "Applies To Smart", type: "boolean", aliases: ["smart"] },
    ],
  },
];

const ALL_FIELDS: CsvField[] = FIELD_GROUPS.flatMap((g) => g.fields);

const PREVIEW_ROWS = 5;

interface ImportRowError {
  row: number;
  ruleId?: string;
  message: string;
}

interface ImportResult {
  inserts: number;
  updates: number;
  total: number;
  errors: ImportRowError[];
}

interface DefaultResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/** Strips everything but letters and digits so "From Year", "from_year" and "FROMYEAR" all match. */
function normaliseHeader(header: string) {
  return header.toLowerCase().replace(/[^a-z0-9]/g, "");
}

const TRUE_VALUES = new Set(["true", "t", "yes", "y", "1", "on", "active", "£", "fixed"]);
const FALSE_VALUES = new Set(["false", "f", "no", "n", "0", "off", "inactive", "%", "percent"]);

/**
 * Coerces whatever the user typed into the literal form CsvHelper expects. A blank stays blank,
 * which the API reads as "clear this field" (or "no change" for the on/off columns).
 */
function coerceValue(raw: string | undefined, type: FieldType): string {
  const value = (raw ?? "").trim();
  if (value === "") return "";

  if (type === "boolean") {
    const key = value.toLowerCase();
    if (TRUE_VALUES.has(key)) return "TRUE";
    if (FALSE_VALUES.has(key)) return "FALSE";
    return value; // let the API report it rather than guessing wrong
  }

  if (type === "number") {
    // Spreadsheets happily export "£1,250.00" or "12%".
    const cleaned = value.replace(/[£$,%\s]/g, "");
    return cleaned === "" || Number.isNaN(Number(cleaned)) ? value : cleaned;
  }

  if (type === "bodyCondition") {
    const key = normaliseHeader(value);
    if (key === "good") return "Good";
    if (key === "average" || key === "avg") return "Average";
    if (key === "any" || key === "heavydamage" || key === "heavy") return "Any";
    return value;
  }

  return value;
}

/** Best-effort match of each API field to a column in the user's file. */
function guessMapping(csvHeaders: string[]): Record<string, string> {
  const byNormalised = new Map<string, string>();
  csvHeaders.forEach((header) => {
    const key = normaliseHeader(header);
    if (key && !byNormalised.has(key)) byNormalised.set(key, header);
  });

  const mapping: Record<string, string> = {};

  ALL_FIELDS.forEach((field) => {
    const candidates = [field.name, field.label, ...(field.aliases ?? [])];

    for (const candidate of candidates) {
      const match = byNormalised.get(normaliseHeader(candidate));
      // Don't let two fields claim the same column - first field wins.
      if (match && !Object.values(mapping).includes(match)) {
        mapping[field.name] = match;
        return;
      }
    }
  });

  return mapping;
}

export default function SpecificPriceCsvModal({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { postDocs } = useApiHelper();
  const queryClient = useQueryClient();

  const [fileName, setFileName] = useState<string | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  /** API field name -> column name in the user's file. */
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const reset = () => {
    setFileName(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setMapping({});
    setParseError(null);
    setImportError(null);
    setImporting(false);
    setResult(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const mappedFields = useMemo(
    () => ALL_FIELDS.filter((f) => mapping[f.name]),
    [mapping]
  );

  const ruleIdColumn = mapping["RuleId"];

  const counts = useMemo(() => {
    if (!ruleIdColumn) return { adds: csvRows.length, updates: 0 };

    let adds = 0;
    csvRows.forEach((row) => {
      if (!(row[ruleIdColumn] ?? "").trim()) adds++;
    });

    return { adds, updates: csvRows.length - adds };
  }, [csvRows, ruleIdColumn]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setImportError(null);
    setResult(null);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: (parsed) => {
        const headers = (parsed.meta?.fields ?? []).filter((h) => h && h.trim() !== "");

        if (headers.length === 0) {
          setParseError("That file doesn't have a header row we can read.");
          return;
        }

        if (parsed.data.length === 0) {
          setParseError("That file has a header row but no data rows.");
          return;
        }

        setFileName(file.name);
        setCsvHeaders(headers);
        setCsvRows(parsed.data);
        setMapping(guessMapping(headers));
      },
      error: (err) => setParseError(err.message),
    });
  };

  const setFieldMapping = (fieldName: string, column: string) => {
    setMapping((current) => {
      const next = { ...current };

      if (!column) {
        delete next[fieldName];
        return next;
      }

      // A column can only feed one field, so drop it from whichever field held it before.
      Object.keys(next).forEach((key) => {
        if (next[key] === column) delete next[key];
      });

      next[fieldName] = column;
      return next;
    });
  };

  /** Rebuilds the file using the API's own header names, with only the columns the user mapped. */
  const buildCanonicalCsv = () => {
    const fields = mappedFields.map((f) => f.name);

    const data = csvRows.map((row) => {
      const out: Record<string, string> = {};
      mappedFields.forEach((field) => {
        out[field.name] = coerceValue(row[mapping[field.name]], field.type);
      });
      return out;
    });

    return Papa.unparse({ fields, data });
  };

  const handleImport = async () => {
    if (mappedFields.length === 0) {
      setImportError("Map at least one column before importing.");
      return;
    }

    setImporting(true);
    setImportError(null);
    setResult(null);

    try {
      const csv = buildCanonicalCsv();
      const formData = new FormData();
      formData.append("file", new File([csv], "import.csv", { type: "text/csv" }));

      const res = await postDocs<DefaultResponse<ImportResult>>(
        "/priceSetting/generic/import",
        formData
      );

      if (!res.success) {
        setImportError(res.message || "Import failed.");
        return;
      }

      setResult(res.data);
      queryClient.invalidateQueries(["priceSettings"]);
      queryClient.invalidateQueries(["pagedTable"]);
    } catch (err: any) {
      setImportError(err?.message ?? "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const previewRows = csvRows.slice(0, PREVIEW_ROWS);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-6xl">
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold">Import Rules from CSV</h1>
          <p className="text-sm text-gray-600">
            Lay your spreadsheet out however you like, then match your columns to the fields below.
            Rows with a blank ID are added as new rules; rows with an ID update that rule.
            Any field you leave unmapped is left untouched.
          </p>
        </div>

        <div>
          <input
            className="block w-full rounded-md border border-gray-300 text-gray-700 shadow-sm file:mr-4 file:rounded-l-md file:border-0 file:bg-primary-600 file:py-2 file:px-4 file:font-semibold file:text-white hover:file:bg-primary-700"
            onChange={handleFile}
            type="file"
            accept=".csv,text/csv"
          />
          {fileName && (
            <p className="mt-2 text-xs text-gray-500">
              {fileName} &mdash; {csvRows.length} row{csvRows.length === 1 ? "" : "s"},{" "}
              {csvHeaders.length} column{csvHeaders.length === 1 ? "" : "s"}
            </p>
          )}
        </div>

        {parseError && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{parseError}</div>
        )}

        {csvHeaders.length > 0 && !result && (
          <>
            {!ruleIdColumn && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>
                  No ID column is mapped, so every row will be added as a brand new rule. Map your
                  ID column if you meant to update existing rules.
                </span>
              </div>
            )}

            <div className="max-h-[45vh] space-y-4 overflow-y-auto pr-1">
              {FIELD_GROUPS.map((group) => (
                <div key={group.title} className="rounded-md border border-gray-200">
                  <div className={`rounded-t-md px-3 py-2 text-sm font-semibold ${group.className}`}>
                    {group.title}
                  </div>

                  <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {group.fields.map((field) => (
                      <div key={field.name}>
                        <label className="block text-xs font-medium text-gray-700">
                          {field.label}
                        </label>

                        <select
                          className={`mt-1 block w-full rounded-md border py-1.5 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 ${
                            mapping[field.name]
                              ? "border-primary-600 bg-primary-50"
                              : "border-gray-300"
                          }`}
                          value={mapping[field.name] ?? ""}
                          onChange={(e) => setFieldMapping(field.name, e.target.value)}
                        >
                          <option value="">-- Ignore --</option>
                          {csvHeaders.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>

                        {field.hint && (
                          <p className="mt-1 text-[11px] leading-tight text-gray-500">{field.hint}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {mappedFields.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold">
                  Preview{" "}
                  <span className="font-normal text-gray-500">
                    (first {Math.min(PREVIEW_ROWS, csvRows.length)} of {csvRows.length} rows)
                  </span>
                </h2>

                <div className="mt-2 overflow-x-auto rounded-md border border-gray-200">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="whitespace-nowrap px-2 py-1 text-left font-semibold">Action</th>
                        {mappedFields.map((field) => (
                          <th
                            key={field.name}
                            className="whitespace-nowrap px-2 py-1 text-left font-semibold"
                          >
                            {field.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, index) => {
                        const isUpdate = !!ruleIdColumn && !!(row[ruleIdColumn] ?? "").trim();

                        return (
                          <tr key={index} className="border-t border-gray-100">
                            <td className="px-2 py-1">
                              <span
                                className={`rounded px-1.5 py-0.5 font-medium ${
                                  isUpdate
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {isUpdate ? "Update" : "Add"}
                              </span>
                            </td>
                            {mappedFields.map((field) => (
                              <td
                                key={field.name}
                                className="whitespace-nowrap px-2 py-1 text-gray-700"
                              >
                                {coerceValue(row[mapping[field.name]], field.type) || (
                                  <span className="text-gray-300">&mdash;</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <p className="mt-2 text-xs text-gray-600">
                  {counts.adds} to add, {counts.updates} to update.
                </p>
              </div>
            )}
          </>
        )}

        {importError && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{importError}</div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md bg-green-50 p-3 text-sm text-green-800">
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <span>
                {result.inserts} rule{result.inserts === 1 ? "" : "s"} added,{" "}
                {result.updates} updated.
              </span>
            </div>

            {result.errors?.length > 0 && (
              <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-medium">
                  {result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped:
                </p>
                <ul className="mt-1 max-h-32 list-disc overflow-y-auto pl-5">
                  {result.errors.map((error, index) => (
                    <li key={index}>
                      Row {error.row}
                      {error.ruleId ? ` (${error.ruleId})` : ""}: {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {importing && <LoadingWheel />}

        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm"
            onClick={() => setOpen(false)}
            disabled={importing}
          >
            {result ? "Close" : "Cancel"}
          </button>

          {!result && (
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-60"
              onClick={handleImport}
              disabled={importing || mappedFields.length === 0}
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {importing ? "Importing..." : `Import ${csvRows.length} Row${csvRows.length === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Downloads every current rule as a CSV using the API's own column names. */
export function useSpecificPriceCsvExport() {
  const { getFile } = useApiHelper();
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);

    try {
      const blob = await getFile("/priceSetting/generic/export");
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = `specific-price-rules-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return { exportCsv, exporting };
}
